import type { DeezerPlayerData, SpotifyPlayerData } from '../grab/types'

// Spotify embeddable entity types and the URL segment that identifies them.
const SPOTIFY_TYPES = ['artist', 'album', 'track', 'playlist', 'episode', 'show'] as const

// Deezer embeddable entity types.
const DEEZER_TYPES = ['artist', 'album', 'track', 'playlist', 'show', 'episode'] as const

export type SpotifyPlayerType = (typeof SPOTIFY_TYPES)[number]
export type DeezerPlayerType = (typeof DEEZER_TYPES)[number]

/**
 * Parse a Spotify share link into structured player data.
 *
 * Accepts the modern `open.spotify.com` host (incl. the `intl-XX` locale segment)
 * and the legacy `play.spotify.com` host. Query strings and trailing slashes are
 * stripped. Returns undefined for unsupported types (e.g. `user`) or malformed links.
 *
 * Example: `https://open.spotify.com/intl-fr/track/11dFghJX9MbiDcXJrWdNNQ?si=…`
 *   → { type: 'track', id: '11dFghJX9MbiDcXJrWdNNQ', uri: '…/embed/track/…' }
 */
export const parseSpotifyLink = (link: string | undefined): SpotifyPlayerData | undefined => {
  if (!link) return undefined
  const match = link.match(/spotify\.com\/(?:intl-[a-z]{2}\/)?(artist|album|track|playlist|episode|show)\/([a-zA-Z0-9]+)/)
  if (!match) return undefined
  const type = match[1] as SpotifyPlayerType
  const id = match[2]
  if (!SPOTIFY_TYPES.includes(type)) return undefined
  return { type, id, uri: `https://open.spotify.com/embed/${type}/${id}` }
}

/**
 * Parse a Deezer share link into structured player data.
 *
 * Accepts `deezer.com` (www or bare), including an optional 2-letter locale
 * segment (e.g. `/fr/`). Returns undefined for unsupported types (e.g.
 * `profile`, `podcast`) or malformed links.
 *
 * Artist widgets point at the top-tracks feed; every other type embeds directly.
 *
 * Example: `https://www.deezer.com/fr/show/1001209741` → { type: 'show', id: '1001209741', uri: '…/widget/dark/show/1001209741' }
 */
export const parseDeezerLink = (link: string | undefined): DeezerPlayerData | undefined => {
  if (!link) return undefined
  const match = link.match(/deezer\.com\/(?:[a-z]{2}\/)?(artist|album|track|playlist|show|episode)\/([a-zA-Z0-9]+)/)
  if (!match) return undefined
  const type = match[1] as DeezerPlayerType
  const id = match[2]
  if (!DEEZER_TYPES.includes(type)) return undefined
  const uri =
    type === 'artist' ? `https://widget.deezer.com/widget/dark/artist/${id}/top_tracks` : `https://widget.deezer.com/widget/dark/${type}/${id}`
  return { type, id, uri }
}
